import { AnimatePresence, motion } from "framer-motion";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { findInputError } from "routes/login/findInputError";
import { isFormInvalid } from "routes/login/isFormValid";
import { LoadingDots } from "utils/loadingAnimation";
import { requestUsernameLogin } from "services/loginRequests";
import { useEffect, useState } from "react";
import {
  emailValidation,
  passwordValidation,
  testEmailValidation,
  usernameValidation,
} from "./loginValidation";
import { MdLockOpen, MdOutlinePerson } from "react-icons/md";
import VisibilityButton from "components/login/VisibilityButton";
import { sanitizeToASCIIPrintable } from "utils/utils";

const LoginForm = () => {
  // States definition
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [pwdVisibility, setPwdVisibility] = useState<boolean>(false);
  const [blockToggle, setBlockToggle] = useState(false);
  const [attemptsLimitError, setAttemptsLimitError] = useState(false);

  // Parameters
  const visibilityTransitionMs = 0.2;

  // Form hooks
  const methods = useForm({ mode: "onBlur" });
  // Redirect hook
  const navigate = useNavigate();

  /**
   * Function executed every time the toggle visibility has changed
   */
  useEffect(() => {
    if (blockToggle) {
      // Toggle the visibility
      setPwdVisibility(!pwdVisibility);

      // Delay the visibility update to allow the exit animation to finish
      setTimeout(() => {
        // And release toggle button
        setBlockToggle(false);
      }, visibilityTransitionMs * 1000 + 10); // delay exceeds the exit animation duration to avoid overclicking errors
    }
  }, [blockToggle]);

  /**
   * Function for submitting
   */
  const handleEmailLogin = methods.handleSubmit(async (programData: any) => {
    // Check if there's currently in a request
    if (loginLoading) return;

    // Block the function
    setLoginLoading(true);

    // Set animation state
    setShowError(false);
    setAttemptsLimitError(false);

    // Format the username
    const dataRequest = {
      ...programData,
      username: sanitizeToASCIIPrintable(programData.username),
    };

    // Send the login information for checking
    const response: {
      successful: boolean;
      message: string;
      twoFactorId?: string;
      attemptLimit: boolean;
    } = await requestUsernameLogin(dataRequest);

    // If the response is successful
    if (!response.successful) {
      // Display an error message
      setShowError(true);
      setAttemptsLimitError(response.attemptLimit);

      // Release the function
      setLoginLoading(false);
      return;
    }

    // Check if there is a twoFactorId
    if (response.twoFactorId) {
      // Then go to the two factor authentication page
      navigate(`/two-factor-authentication/${response.twoFactorId}`, {
        state: { fromApp: true, remember: programData.remember },
      });
    } else {
      // Then go to the home page
      navigate("/home");
    }

    // Release the function
    setLoginLoading(false);
  });

  // Get the errors
  const usernameError: { [k: string]: any } = findInputError(
    methods.formState.errors,
    "username"
  );
  const passwordError: { [k: string]: any } = findInputError(
    methods.formState.errors,
    "password"
  );

  /**
   * Timer for the username and password mismatch
   */
  useEffect(() => {
    if (showError) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        setShowError(false);
        setAttemptsLimitError(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showError]);

  return (
    <FormProvider {...methods}>
      <form
        id="login-form"
        className="w-full"
        onSubmit={(event) => event.preventDefault()}
        noValidate
        autoComplete="off"
      >
        <div className="self-stretch h-full flex-col justify-start items-start gap-6 flex">
          <div className="w-full">
            <div className="self-stretch h-[71px] flex-col justify-start items-start gap-1.5 flex">
              <div className="rounded justify-start items-start gap-2 inline-flex">
                <label
                  htmlFor="username"
                  className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
                >
                  Username
                </label>
              </div>
              <div className="w-full h-full rounded-lg bg-neutral-50 text-neutral-700 placeholder:text-neutral-300 text-base font-normal font-sans leading-[19px] flex items-center relative">
                <MdOutlinePerson className="absolute left-3.5 w-5 h-5 z-[5] text-neutral-500" />
                <input
                  // {...methods.register("email", emailValidation)}
                  {...methods.register("username", usernameValidation)}
                  // {...methods.register("email", testEmailValidation)}
                  id="username"
                  type="text"
                  className="w-full h-full pl-[42px] pr-4 rounded-lg bg-transparent text-neutral-700 placeholder:text-neutral-300 placeholder:select-none focus:outline-none text-base font-normal font-sans leading-[19px] z-10"
                  placeholder="Enter your username"
                  autoComplete="username"
                />
              </div>
            </div>
            {/* Error message */}
            <AnimatePresence mode="wait" initial={false}>
              {isFormInvalid(usernameError) && (
                <motion.div
                  className="w-full py-1 text-error-600 text-sm font-normal font-sans leading-[17px] select-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {usernameError.error.message}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="w-full">
            <div className="self-stretch h-[71px] flex-col justify-start items-start gap-1.5 flex">
              <div className="rounded justify-start items-start gap-2 inline-flex">
                <label
                  htmlFor="password"
                  className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
                >
                  Password
                </label>
              </div>
              <div className="w-full h-full rounded-lg bg-neutral-50 text-neutral-700 placeholder:text-neutral-300 text-base font-normal font-sans leading-[19px] flex items-center relative">
                <MdLockOpen className="absolute left-3.5 w-5 h-5 text-neutral-500 z-[5]" />
                <input
                  {...methods.register("password", passwordValidation)}
                  id="password"
                  type={pwdVisibility ? "text" : "password"}
                  className="w-full h-full pl-[42px] pr-12 rounded-lg bg-transparent text-neutral-700 placeholder:text-neutral-300 placeholder:select-none focus:outline-none text-base font-normal font-sans leading-[19px] z-10"
                  placeholder="Enter your password"
                  autoComplete="password"
                />
                <VisibilityButton
                  visibility={pwdVisibility}
                  setVisibility={setPwdVisibility}
                />
              </div>
            </div>
            {/* Error message */}
            <AnimatePresence mode="wait" initial={false}>
              {isFormInvalid(passwordError) && (
                <motion.div
                  key="error-message"
                  className="w-full py-1 text-error-600 text-sm font-normal font-sans leading-[17px] select-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  A valid password is required
                </motion.div>
              )}
            </AnimatePresence>
            {/* Message attempts */}
            <AnimatePresence mode="wait" initial={false}>
              {showError && (
                <motion.div
                  key="error-message-attempts"
                  className="w-full py-1 text-error-600 text-sm font-normal font-sans leading-[17px] select-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {attemptsLimitError
                    ? "This client has reached the limit of attempts. Please wait to try again."
                    : "Username or password is incorrect. Please try again."}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="self-stretch h-full flex-col justify-start items-start gap-3 flex">
            <div className="w-full h-full flex items-center">
              <div className="group w-fit h-full flex justify-start items-center gap-1 text-lg">
                <input
                  {...methods.register("remember")}
                  type="checkbox"
                  id="remember"
                  name="remember"
                  className="flex-1 w-4 h-4 accent-neutral-700 group-hover:cursor-pointer"
                />
                <label
                  htmlFor="remember"
                  className="text-neutral-700 group-hover:text-neutral-900 group-hover:cursor-pointer text-base font-sans leading-[17px] select-none"
                >
                  Remember me
                </label>
              </div>
              <div className="flex-1" />
              <Link
                to="/reset-password"
                className="w-fit h-full items-center gap-2 flex"
              >
                <div className="text-neutral-700 text-base font-normal hover:text-neutral-900 font-sans underline leading-[17px] select-none">
                  Forgot Password?
                </div>
              </Link>
            </div>
          </div>
          <button
            type="submit"
            className="self-stretch px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-100 rounded justify-center items-center gap-2 inline-flex"
            onClick={handleEmailLogin}
          >
            <div className="text-white text-lg font-semibold font-sans leading-snug select-none">
              {loginLoading ? <LoadingDots /> : "Sign in"}
            </div>
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

export default LoginForm;
